import { useEffect, useState } from "react";
import api from "../utils/api";
import Modal from "../components/Modal";
import EditEarning from "../components/earning/EditEarning";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";

export default function EarningList() {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState({ text: "", date: "" });
  const [textSearchInput, setTextSearchInput] = useState("");
  const [dateSearchInput, setDateSearchInput] = useState("");

  const itemsPerPage = 10;

  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [selectedEarning, setSelectedEarning] = useState(null);

  const fetchEarnings = async (page = 1, text = "", date = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/earnings/user/list`, {
        params: { page, limit: itemsPerPage, text, date },
        withCredentials: true,
      });
      setEarnings(res.data.earnings || []);

      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
        setCurrentPage(res.data.pagination.currentPage || page);
      }
    } catch (err) {
      console.error("Error fetching earnings:", err);
      setEarnings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    fetchEarnings(currentPage, search.text, search.date);
  }, [currentPage, search]);

  const deleteEarning = async (id) => {
    if (!window.confirm("Are you sure you want to delete this earning?"))
      return;

    try {
      await api.delete(`/earnings/${id}`, { withCredentials: true });
      if (earnings.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchEarnings(currentPage, search.text, search.date);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete earning!");
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch({ text: textSearchInput, date: dateSearchInput });
  };

  const handleClear = () => {
    setTextSearchInput("");
    setDateSearchInput("");
    setCurrentPage(1);
    setSearch({ text: "", date: "" });
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedEarning && (
          <EditEarning
            earning={selectedEarning}
            onSuccess={async () => {
              setIsModalOpenForEdit(false);
              fetchEarnings(currentPage, search.text, search.date);
            }}
          />
        )}
      </Modal>

      <div>
        <h1 className="text-2xl font-bold mt-10 ml-2 mb-3">All Earnings</h1>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <div className="mb-4 flex justify-end gap-2">
            <input
              type="text"
              value={textSearchInput}
              onChange={(e) => setTextSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by content owner..."
              className="w-96 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateSearchInput}
              onChange={(e) => setDateSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
            >
              Clear
            </button>
          </div>

          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl">
                  Content Owner
                </th>
                <th className="px-4 py-3 text-center">Date</th>
                <th className="px-4 py-3 text-center">Total Earning</th>
                <th className="px-4 py-3 text-center">Platform Commission</th>

                {userRole && ["Admin", "Manager"].includes(userRole) ? (
                  <>
                    <th className="px-4 py-3 text-center">Net Earning</th>
                    <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                      Actions
                    </th>
                  </>
                ) : (
                  <th className="px-4 py-3 text-center rounded-tr-xl rounded-br-xl">
                    Net Earning
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    Loading earnings...
                  </td>
                </tr>
              ) : earnings.length ? (
                earnings.map((earning) => (
                  <tr
                    key={earning._id}
                    className="border-t transition hover:bg-slate-100"
                  >
                    <td className="px-4 py-4 font-medium">
                      {earning.contentOwnerId?.name || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {new Date(earning.date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-4 text-center">
                      ${earning.totalEarning}
                    </td>
                    <td className="px-4 py-4 text-center">
                      ${earning.platformCommission}
                    </td>
                    <td className="px-4 py-4 text-center font-semibold">
                      ${earning.netEarning}
                    </td>
                    {userRole && ["Admin", "Manager"].includes(userRole) && (
                      <td className="px-4 py-4 flex justify-end gap-4">
                        <EditBtn
                          onClick={() => {
                            setSelectedEarning(earning);
                            setIsModalOpenForEdit(true);
                          }}
                          label="Edit Earning"
                        />
                        <DeleteBtn
                          onClick={() => deleteEarning(earning._id)}
                          label="Delete Earning"
                        />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No earnings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
